import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectSelectedAnimationDetails } from "../../../app/slices/AnimationsSlice";
import PiButton from "../../../components/PiButton";
import {
  getAnimationDisplayLabel,
  getAnimationStoredName,
} from "./animationDisplay";
import { useAnimationRender } from "./useAnimationAssets.hook";

export default function AnimationLibraryDetailsColumn(props: {
  onSubmit: () => void;
}) {
  const { onSubmit } = props;
  const details = useSelector(selectSelectedAnimationDetails);
  const { renderUri, isFetching: isFetchingRender } = useAnimationRender(
    details?.externalId
  );
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [details?.externalId, renderUri]);

  const noImage = (
    <div className="flex h-full w-full">
      <div className="m-auto text-[96px] text-creator-text-soft">
        <DirectionsWalkOutlinedIcon fontSize="inherit" color="inherit" />
      </div>
    </div>
  );

  const image = renderUri && !hasImageError && (
    <div className="relative h-full w-full overflow-hidden">
      {((isFetchingRender && !renderUri) ||
        (!!renderUri && !isImageLoaded && !hasImageError)) && (
        <div className="absolute inset-0 animate-pulse bg-creator-surface" />
      )}
      <img
        src={renderUri}
        alt={details?.name ?? "Animation preview"}
        className={`h-full w-full object-contain rounded-[5px] p-[8px] transition-opacity ${
          isImageLoaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={() => setIsImageLoaded(true)}
        onError={() => {
          setHasImageError(true);
          setIsImageLoaded(false);
        }}
      />
    </div>
  );

  const detailsPreview = renderUri && !hasImageError ? image : noImage;

  return (
    <div className="relative flex h-full w-full select-none flex-col overflow-hidden rounded-[5px] bg-interaction-softBackground p-[18px] dark:bg-[#15191d]">
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[5px] bg-[#DAE0E6] dark:bg-[#0f1216]">
        {details && isFetchingRender && !renderUri && (
          <div className="absolute inset-0 animate-pulse bg-creator-surface" />
        )}
        {detailsPreview}
      </div>
      <div className="mt-[16px] flex shrink-0 items-end justify-between gap-[16px]">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[24px] font-bold text-creator-text">
            {details ? getAnimationStoredName(details) : "Animation Library"}
          </div>
          <div className="mt-[4px] truncate text-[15px] font-medium text-creator-text-sub">
            {getAnimationDisplayLabel(details)}
          </div>
        </div>
        {details && (
          <div className="flex shrink-0 gap-[32px] rounded-[5px] bg-creator-surface/70 px-[18px] py-[12px] dark:bg-[#1b2026]">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-creator-text-sub">
                Layer
              </div>
              <div className="mt-[4px] text-[18px] font-bold text-creator-text">
                {details.layer}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-creator-text-sub">
                State
              </div>
              <div className="mt-[4px] max-w-[240px] truncate text-[18px] font-bold text-creator-text">
                {getAnimationDisplayLabel(details)}
              </div>
            </div>
          </div>
        )}
      </div>
      {details && (
        <div className="mt-[16px] h-[44px] w-[220px] shrink-0 self-end">
          <PiButton variant="piBlue" onClick={onSubmit}>
            Change Animation
          </PiButton>
        </div>
      )}
    </div>
  );
}
